import { useEffect, useState } from 'react';
import { useGetJsonCmsContentLazyQuery } from '../../../generated/graphqlModels';
import {
  JsonCmsContent,
  JsonCmsContentDto,
  PluginType,
} from '../types/JsonCmsContent';

const contents: Record<string, JsonCmsContentDto> = {};

const useJsonCmsContent = (contentId: string, page: number = 1) => {
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState<JsonCmsContent | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [isPresentation, setIsPresentation] = useState(false);
  const [getJsonCmsContent] = useGetJsonCmsContentLazyQuery();

  useEffect(() => {
    if (contents[contentId]?.results[page]) {
      const content = contents[contentId]!;
      setContent(content.results[page]!);
      setNumPages(content.totalPages);
      setIsPresentation(content.isPresentation);
    } else {
      setIsLoading(true);
      getJsonCmsContent({
        variables: {
          contentPk: contentId,
          page,
        },
      })
        .then((response) => {
          const responseData = response.data?.getJsonCmsContent;
          if (!responseData) {
            return;
          }
          const data = JSON.parse(JSON.stringify(responseData));
          if (!contents[contentId]) {
            contents[contentId] = data as any;
          }
          if (page === -1) {
            setContent({
              plugin: 'ContentSectionPlugin',
              data: {},
              children: data.results,
            } as any);
          } else {
            contents[contentId]!.results[data.currentPage] = data
              .results[0]! as any;
            setContent(data.results[0]! as any);
          }
          setNumPages(data.totalPages);
          setIsPresentation(data.isPresentation);
        })
        .finally(() => setIsLoading(false));
    }
  }, [contentId, page]);

  const prefetchContent = async (pageNumber: number) => {
    await getJsonCmsContent({
      variables: {
        contentPk: contentId,
        page: pageNumber,
      },
    });
  };

  const getPreviousContent = async () => {
    await prefetchContent(page - 1);
  };

  const getNextContent = async () => {
    await prefetchContent(page + 1);
  };

  const isAssessment =
    numPages === 1 &&
    content?.children?.find((c) => c.plugin === PluginType.AssessmentPlugin);

  const numAssessmentQuestions =
    (isAssessment
      ? content?.children?.find(
          (c) => c.plugin === PluginType.AssessmentPlugin,
        )!.data?.questions?.length
      : 0) || 0;

  return {
    content,
    numPages,
    isLoading,
    isPresentation,
    isAssessment,
    numAssessmentQuestions,
    getPreviousContent,
    getNextContent,
  };
};

export default useJsonCmsContent;
